import type { TeamMemberResponse, TeamResponse } from "@/api";
import { useCurrentUser, usePermissions } from "@/features/account/useCurrentUser";

export const TEAM_ROLES = ["team_admin", "member", "viewer"] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];
export const asTeamRole = (role: string): TeamRole =>
  (TEAM_ROLES as readonly string[]).includes(role) ? (role as TeamRole) : "member";

export interface TeamAccess {
  /** Add, remove and change people's roles: platform admins and the team's admins. The API enforces it. */
  canManageMembers: boolean;
  /** Rename, describe and delete: platform admins only. */
  canEditTeam: boolean;
  myUuid: string | undefined;
}

/** What the signed-in person can do with this team (their team role comes from /auth/me). */
export function useTeamAccess(team: TeamResponse | undefined): TeamAccess {
  const me = useCurrentUser().data;
  const { isPlatformAdmin } = usePermissions();
  const myRole = me?.teams.find((m) => m.team.uuid === team?.uuid)?.role;
  return {
    canManageMembers: isPlatformAdmin || myRole === "team_admin",
    canEditTeam: isPlatformAdmin,
    myUuid: me?.user.uuid,
  };
}

/** The team's only admin can't be demoted (the API refuses it for yourself, and a team needs one). */
export function isOnlyAdmin(member: TeamMemberResponse, members: TeamMemberResponse[]): boolean {
  return member.role === "team_admin" && members.filter((m) => m.role === "team_admin").length === 1;
}
